import { useState } from 'react';
import {
  Bell,
  Mail,
  Smartphone,
  Plus,
  X,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  FormModal,
  FormField,
  FormInput,
} from '@/components/ui/form-modal';
import type {
  AlertConfig,
  TemperatureThresholds,
  StorageLocationWithTemp,
} from '../../types/storage';

interface AlertConfigModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly location: StorageLocationWithTemp;
  readonly onSaveAlerts: (config: AlertConfig) => void;
  readonly onSaveThresholds: (thresholds: TemperatureThresholds) => void;
  readonly isSaving: boolean;
}

export function AlertConfigModal({
  isOpen,
  onClose,
  location,
  onSaveAlerts,
  onSaveThresholds,
  isSaving,
}: AlertConfigModalProps) {
  const [alertConfig, setAlertConfig] = useState<AlertConfig>(location.alertConfig);
  const [thresholds, setThresholds] = useState<TemperatureThresholds>(location.thresholds);
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [activeTab, setActiveTab] = useState<'notifications' | 'thresholds'>('notifications');

  const handleAddEmail = () => {
    if (newEmail && !alertConfig.emailRecipients.includes(newEmail)) {
      setAlertConfig({
        ...alertConfig,
        emailRecipients: [...alertConfig.emailRecipients, newEmail],
      });
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (email: string) => {
    setAlertConfig({
      ...alertConfig,
      emailRecipients: alertConfig.emailRecipients.filter((e) => e !== email),
    });
  };

  const handleAddPhone = () => {
    if (newPhone && !alertConfig.smsRecipients.includes(newPhone)) {
      setAlertConfig({
        ...alertConfig,
        smsRecipients: [...alertConfig.smsRecipients, newPhone],
      });
      setNewPhone('');
    }
  };

  const handleRemovePhone = (phone: string) => {
    setAlertConfig({
      ...alertConfig,
      smsRecipients: alertConfig.smsRecipients.filter((p) => p !== phone),
    });
  };

  const handleSave = () => {
    onSaveAlerts(alertConfig);
    onSaveThresholds(thresholds);
  };

  return (
    <FormModal
      title={`Alert Settings - ${location.name}`}
      description="Configure temperature alerts and notification preferences"
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      saveLabel={isSaving ? 'Saving...' : 'Save Settings'}
    >
      <div className="space-y-6">
        <div className="flex gap-2 border-b">
          <button
            type="button"
            onClick={() => setActiveTab('notifications')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'notifications'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Bell className="mr-2 inline h-4 w-4" />
            Notifications
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('thresholds')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'thresholds'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Temperature Thresholds
          </button>
        </div>

        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-gray-600" />
                <div>
                  <p className="font-medium">Enable Alerts</p>
                  <p className="text-sm text-gray-500">
                    Receive notifications when temperature goes out of range
                  </p>
                </div>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={alertConfig.enabled}
                  onChange={(e) =>
                    setAlertConfig({
                      ...alertConfig,
                      enabled: e.target.checked,
                    })
                  }
                  className="peer sr-only"
                />
                <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white" />
              </label>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-gray-600" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">Email Notifications</p>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={alertConfig.emailNotifications}
                        onChange={(e) =>
                          setAlertConfig({
                            ...alertConfig,
                            emailNotifications: e.target.checked,
                          })
                        }
                        className="peer sr-only"
                      />
                      <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white" />
                    </label>
                  </div>
                </div>
              </div>

              {alertConfig.emailNotifications && (
                <div className="ml-8 space-y-2">
                  <div className="flex gap-2">
                    <FormInput
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Add email address"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddEmail}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {alertConfig.emailRecipients.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => handleRemoveEmail(email)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Smartphone className="h-5 w-5 text-gray-600" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">SMS Notifications</p>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={alertConfig.smsNotifications}
                        onChange={(e) =>
                          setAlertConfig({
                            ...alertConfig,
                            smsNotifications: e.target.checked,
                          })
                        }
                        className="peer sr-only"
                      />
                      <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white" />
                    </label>
                  </div>
                </div>
              </div>

              {alertConfig.smsNotifications && (
                <div className="ml-8 space-y-2">
                  <div className="flex gap-2">
                    <FormInput
                      type="tel"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      placeholder="Add phone number"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddPhone()}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddPhone}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {alertConfig.smsRecipients.map((phone) => (
                      <span
                        key={phone}
                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm"
                      >
                        {phone}
                        <button
                          type="button"
                          onClick={() => handleRemovePhone(phone)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 rounded-lg border p-4">
              <Clock className="h-5 w-5 text-gray-600" />
              <div className="flex-1">
                <p className="font-medium">Escalation Delay</p>
                <p className="text-sm text-gray-500">
                  Time before escalating unacknowledged alerts
                </p>
              </div>
              <div className="flex items-center gap-2">
                <FormInput
                  type="number"
                  min="1"
                  max="60"
                  value={alertConfig.escalationDelayMinutes}
                  onChange={(e) =>
                    setAlertConfig({
                      ...alertConfig,
                      escalationDelayMinutes: parseInt(e.target.value, 10) || 15,
                    })
                  }
                  className="w-20"
                />
                <span className="text-sm text-gray-500">minutes</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'thresholds' && (
          <div className="space-y-6">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <h4 className="font-medium text-red-800">Critical Thresholds</h4>
              <p className="mb-4 text-sm text-red-600">
                Immediate alert when temperature exceeds these limits
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Min Critical (°C)">
                  <FormInput
                    type="number"
                    value={thresholds.minCritical}
                    onChange={(e) =>
                      setThresholds({
                        ...thresholds,
                        minCritical: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </FormField>
                <FormField label="Max Critical (°C)">
                  <FormInput
                    type="number"
                    value={thresholds.maxCritical}
                    onChange={(e) =>
                      setThresholds({
                        ...thresholds,
                        maxCritical: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </FormField>
              </div>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <h4 className="font-medium text-amber-800">Warning Thresholds</h4>
              <p className="mb-4 text-sm text-amber-600">
                Early warning when temperature approaches critical levels
              </p>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Min Warning (°C)">
                  <FormInput
                    type="number"
                    value={thresholds.minWarning}
                    onChange={(e) =>
                      setThresholds({
                        ...thresholds,
                        minWarning: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </FormField>
                <FormField label="Max Warning (°C)">
                  <FormInput
                    type="number"
                    value={thresholds.maxWarning}
                    onChange={(e) =>
                      setThresholds({
                        ...thresholds,
                        maxWarning: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </FormField>
              </div>
            </div>

            <div className="rounded-lg border bg-gray-50 p-4">
              <h4 className="mb-2 font-medium text-gray-700">Visual Preview</h4>
              <div className="flex h-8 overflow-hidden rounded">
                <div
                  className="bg-red-300"
                  style={{ width: '15%' }}
                  title={`Below ${thresholds.minCritical}°C`}
                />
                <div
                  className="bg-amber-300"
                  style={{ width: '10%' }}
                  title={`${thresholds.minCritical}°C to ${thresholds.minWarning}°C`}
                />
                <div
                  className="bg-green-300"
                  style={{ width: '50%' }}
                  title={`${thresholds.minWarning}°C to ${thresholds.maxWarning}°C (Normal)`}
                />
                <div
                  className="bg-amber-300"
                  style={{ width: '10%' }}
                  title={`${thresholds.maxWarning}°C to ${thresholds.maxCritical}°C`}
                />
                <div
                  className="bg-red-300"
                  style={{ width: '15%' }}
                  title={`Above ${thresholds.maxCritical}°C`}
                />
              </div>
              <div className="mt-2 flex justify-between text-xs text-gray-500">
                <span>{thresholds.minCritical}°</span>
                <span>{thresholds.minWarning}°</span>
                <span>Normal Range</span>
                <span>{thresholds.maxWarning}°</span>
                <span>{thresholds.maxCritical}°</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </FormModal>
  );
}
